<template>
    <div class="location-autocomplete">
        <AutoComplete
            :id="id"
            v-model="location"
            :data-testid="dataTestid"
            :suggestions="requestedCountries"
            option-label="formattedAddress"
            :placeholder="props.placeholder && ($t(props.placeholder) as string)"
            :empty-search-message="$t('generic.no.results.found')"
            force-selection
            scroll-height="40vh"
            :auto-option-focus="false"
            :disabled="props.disabled"
            :append-to="appendToElement"
            :invalid="props.invalid"
            @complete="handleComplete"
            @item-select="handlerChange"
            @clear="handleClear"
            @keydown.escape.stop
        ></AutoComplete>
        <VJoyIcon class="location-autocomplete__icon" name="location" color="neutral" />
    </div>
</template>

<script lang="ts" setup>
    import AutoComplete, {type AutoCompleteCompleteEvent, type AutoCompleteOptionSelectEvent} from 'primevue/autocomplete';
    import {ref, useLocale, useTranslation, watch} from '#imports';
    import {pushVJoySnackbar, VJoyIcon} from '@maltjoy/core-vue';
    import type {PostalAddress} from '@api';
    import {useLocationAutocomplete} from '~/composables/useLocationAutocomplete';
    import type {TranslationKey} from '#build/i18n';

    const props = withDefaults(
        defineProps<{
            modelValue?: Partial<PostalAddress> | string | null;
            disabled: boolean;
            placeholder?: TranslationKey;
            id?: string;
            dataTestid?: string;
            appendToElement?: string;
            invalid?: boolean;
        }>(),
        {
            modelValue: null,
            id: 'location-autocomplete',
            dataTestid: 'location-autocomplete',
            placeholder: 'generic.city.label',
            appendToElement: '.profile-id-or-alias',
            invalid: false,
        },
    );

    const emits = defineEmits<{
        (e: 'update:modelValue', value: PostalAddress | undefined): void;
    }>();

    // Composables
    const {t} = useTranslation();
    const {search} = useLocationAutocomplete();
    const {locale} = useLocale();

    // Refs
    const location = ref(props.modelValue);
    const requestedCountries = ref();

    // Handlers
    function handlerChange(event: AutoCompleteOptionSelectEvent) {
        emits('update:modelValue', event.value);
    }

    function handleClear() {
        if (location.value === '') {
            emits('update:modelValue', undefined);
        }
    }

    async function handleComplete(event: AutoCompleteCompleteEvent) {
        try {
            requestedCountries.value = await search(event.query, locale.value);
        } catch (error: any) {
            pushVJoySnackbar({
                props: {
                    message: t('generic.error.unexpected'),
                    level: 'error',
                },
            });
        }
    }

    watch(
        () => props.modelValue,
        (value) => {
            location.value = value;
        },
    );
</script>
<style lang="scss">
    @import 'primeicons/primeicons.css';
    .location-autocomplete {
        position: relative;

        > .p-inputwrapper .p-inputtext {
            padding-left: var(--joy-core-spacing-10) !important;
        }
    }

    .location-autocomplete__icon {
        z-index: 2;
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        left: var(--joy-core-spacing-5);
    }
</style>
